import {memo, useState, useCallback} from 'react';
import {useTranslate} from 'src/locales';
import Stack from '@mui/material/Stack';
import Collapse from '@mui/material/Collapse';
import ListSubheader from '@mui/material/ListSubheader';
import {useNavigate} from 'react-router-dom';
import NavList from './nav-list';
import {NavProps, NavGroupProps} from '../types';
import AuthUtils from 'src/utils/AuthUtils';
import {IoChevronBackCircleOutline} from 'react-icons/io5';
import {
  BASE_MENU_ITEM,
  BASE_ROUTE,
  BASE_ROUTE_MODULE,
  BASE_ROUTE_SUB_MODULE,
  IS_FROM_EPIC,
  IS_FROM_ATHENA,
  MODE
} from 'src/routes/BaseRoute';
import EncryptUtils from 'src/utils/EncryptUtils';
import FormConstants from '@shared/constants/FormConstants';
import SubModuleNavList from './submodule-nav-list';
import {environment} from '@shared/config/environment';

// ----------------------------------------------------------------------

function NavSectionVertical({
  data,
  slotProps,
  module,
  menuItem,
  renderCountBadge,
  menuListData,
  filtersData,
  setExternalUrl,
  setVisitDetails,
  ...other
}: NavProps) {
  return (
    <Stack component="nav" id="nav-section-vertical" {...other}>
      {data?.map((group, index) => (
        <Group
          key={group.subheader || index}
          subheader={group.subheader}
          moduleName={group.moduleName}
          items={group.items ?? []}
          slotProps={slotProps}
          module={module}
          menuItem={menuItem}
          renderCountBadge={renderCountBadge}
          menuListData={menuListData}
          filtersData={filtersData}
          setExternalUrl={setExternalUrl}
          setVisitDetails={setVisitDetails}
        />
      ))}
    </Stack>
  );
}

export default memo(NavSectionVertical);

// ----------------------------------------------------------------------

function Group({
  subheader,
  moduleName,
  items,
  slotProps,
  module,
  menuItem,
  renderCountBadge,
  menuListData,
  filtersData,
  setExternalUrl,
  setVisitDetails
}: NavGroupProps) {
  const [open, setOpen] = useState(true);
  const {t} = useTranslate();
  const navigate = useNavigate();
  const isFromEpic = EncryptUtils.getDecryptedData(
    location?.search,
    IS_FROM_EPIC
  );
  const isFromAthena = EncryptUtils.getDecryptedData(
    location?.search,
    IS_FROM_ATHENA
  );
  const mode = EncryptUtils.getDecryptedData(location?.search, MODE);

  const handleToggle = useCallback(() => {
    setOpen(prev => !prev);
  }, []);

  const renderContent = items.map(list => {
    if (moduleName && BASE_ROUTE_SUB_MODULE === 'submodule') {
      return (
        <SubModuleNavList
          key={list.name}
          data={list}
          depth={1}
          slotProps={slotProps}
          renderCountBadge={renderCountBadge}
          menuListData={menuListData}
          filtersData={filtersData}
          setVisitDetails={setVisitDetails}
        />
      );
    } else {
      return (
        <NavList
          key={list.name}
          data={list}
          depth={1}
          slotProps={slotProps}
          setExternalUrl={setExternalUrl}
        />
      );
    }
  });

  const goBackToModules = (): void => {
    let url = `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${module}`;

    const ignoreMenuItems = ['user-log'];

    const checkCondition =
      module == 'pharmacy' && ignoreMenuItems.includes(menuItem);

    if (menuItem && !checkCondition) {
      url += `?${BASE_MENU_ITEM}=${menuItem}`;
    }
    navigate(url);
    sessionStorage.removeItem('@searchQuery');
    sessionStorage.removeItem('@toastPath');
  };

  return (
    <Stack sx={{px: 2}}>
      {subheader ? (
        <>
          <ListSubheader
            disableGutters
            disableSticky
            onClick={handleToggle}
            sx={{
              fontSize: 11,
              cursor: 'pointer',
              typography: 'overline',
              display: 'inline-flex',
              color: 'text.disabled',
              mb: `${slotProps?.gap || 4}px`,
              p: theme => theme.spacing(2, 1, 1, 1.5),
              transition: theme =>
                theme.transitions.create(['color'], {
                  duration: theme.transitions.duration.shortest
                }),
              '&:hover': {
                color: `${!environment?.IS_LMS_ENABLED_FOR_EXTERNAL_SITE_AS_EMBEDDED && 'background.paper'}`
              },
              ...slotProps?.subheader
            }}>
            {t(subheader)}
          </ListSubheader>

          <Collapse in={open}>{renderContent}</Collapse>
        </>
      ) : moduleName && BASE_ROUTE_SUB_MODULE === 'submodule' ? (
        <>
          {(isFromEpic || isFromAthena) &&
          mode === FormConstants.STATIC_VALUES.VIEW ? (
            <div />
          ) : (
            <ListSubheader
              disableGutters
              disableSticky
              onClick={() => {
                goBackToModules();
                AuthUtils.clearSelectedCheckBoxData();
                AuthUtils.clearSelectedCheckBoxList();
                // AuthUtils.clearAdditionalSelectedCheckBoxList();
                // AuthUtils.clearPatientAccountingSelectedCheckBoxList();
                // AuthUtils.clearSelectedAdditionalCheckBoxData();
                // AuthUtils.clearSelectedPatientAccountingCheckBoxData();
              }}
              sx={{
                fontSize: 11,
                cursor: 'pointer',
                typography: 'overline',
                display: 'flex',
                alignItems: 'center',
                color: 'text.disabled',
                mb: `${slotProps?.gap || 4}px`,
                p: theme => theme.spacing(2, 1, 1, 1.5),
                transition: theme =>
                  theme.transitions.create(['color'], {
                    duration: theme.transitions.duration.shortest
                  }),
                '&:hover': {
                  color: 'background.paper'
                },
                gap: 2,
                ...slotProps?.moduleName
              }}>
              <IoChevronBackCircleOutline size={24} />
              {t(moduleName)}
            </ListSubheader>
          )}
          {renderContent}
        </>
      ) : (
        renderContent
      )}
    </Stack>
  );
}

